import React, { useCallback, useMemo } from "react";
import _ from "lodash";

import { Column, UTMInspection } from "../../../../utils/models";
import { getReadingStyle, getUTMColorCodes } from "../../../../utils/data";
import { calculateWallLossPercentage, calculateWallLossValue, getChunkedData } from "../../../../utils/utilities";

import Conditional from "../../../common/conditional";
import CustomSection from "../../custom-general/custom-section";
import PDFTablePage from "../../../themed/pdf/pdfTablePage";

import styles from "./style.module.scss";

interface Props {
    displayExpressSummary?: boolean;
    summaryTitle?: string;
    expressSummaryTitle?: string;
    expressSummarySubtitle?: string;
    corrosionAllowance: number;
    inspectionItems: UTMInspection[];
}

interface UTMItem {
    tmlNumber: number;
    nominalThickness: number;
    description: string;
    maxThickness: number | 'N/A';
    minThickness: number | 'N/A';
    wallLoss: number | string;
    wallLossPercentage: number | string;
}

const CustomUTMBreakdown: React.FC<Props> = ({ corrosionAllowance, inspectionItems, displayExpressSummary = true, expressSummarySubtitle, expressSummaryTitle = 'UTM Express Summary', summaryTitle = 'UTM Summary'}) => {
    const COLUMNS: Column[] = useMemo(() => {
        return [
            {
                path: 'description',
                label: 'Description'
            },
            {
                path: 'nominalThickness',
                label: 'Nominal Thickness (mm)',
                content: (item) => item.nominalThickness || 'N/A',
            },
            {
                path: 'minThickness',
                label: 'Min. Thickness (mm)',
                style: (item: UTMItem) => typeof item.minThickness === 'number' ? getReadingStyle(+item.nominalThickness, +corrosionAllowance, item.minThickness, getUTMColorCodes()) as React.CSSProperties : {},
            },
            {
                path: 'maxThickness',
                label: 'Max. Thickness (mm)',
                style: (item: UTMItem) => typeof item.maxThickness === 'number' ? getReadingStyle(+item.nominalThickness, +corrosionAllowance, item.maxThickness, getUTMColorCodes()) as React.CSSProperties : {},
            },
            {
                path: 'wallLoss',
                label: 'Wall Loss (mm)',
                style: (item: UTMItem) => typeof item.maxThickness === 'number' ? getReadingStyle(+item.nominalThickness, +corrosionAllowance, item.maxThickness, getUTMColorCodes()) as React.CSSProperties : {},
            },
            {
                path: 'wallLossPercentage',
                label: 'Wall Loss (%)',
                style: (item: UTMItem) => typeof item.maxThickness === 'number' ? getReadingStyle(+item.nominalThickness, +corrosionAllowance, item.maxThickness, getUTMColorCodes()) as React.CSSProperties : {},
            },
        ];
    }, [corrosionAllowance]);

    const COLUMNS_WITH_TML_LOCATION = useMemo(() => {
        const tmlLocationColumn: Column = {
            path: 'tmlNumber',
            label: 'TML Number',
        };

        return [tmlLocationColumn, ...COLUMNS];
    }, [COLUMNS]);

    const getReadingsForUTMItem = useCallback((item: UTMInspection['items'][0]) => {
        const readings = _.omit(item, ['comments', 'description', 'nominal', 'size', 'tmlLocation']);
        const parsedReadings = Object.values(readings).map((reading) => parseFloat(reading as string)).filter((reading) => !isNaN(reading));

        return parsedReadings;
    }, []);

    const totalInspectedItems = useMemo(() => {
        return inspectionItems.flatMap((item) => item.items).flatMap((item) => getReadingsForUTMItem(item)).length;
    }, [inspectionItems, getReadingsForUTMItem]);

    const mapUTMInspectionToBreakdown = useCallback((item: UTMInspection['items'][0]): UTMItem => {
        const parsedReadings = getReadingsForUTMItem(item);
        
        if (parsedReadings.length > 0) {
            const minThickness = Math.min(...parsedReadings);
            const maxThickness = Math.max(...parsedReadings);
    
            return {
                tmlNumber: item.tmlLocation,
                nominalThickness: item.nominal,
                description: item.description,
                maxThickness,
                minThickness,
                wallLoss: isNaN(item.nominal) ? 'N/A' : calculateWallLossValue(item.nominal, minThickness).toFixed(2),
                wallLossPercentage: calculateWallLossPercentage(item.nominal, minThickness),
            };
        }


        return {
            tmlNumber: item.tmlLocation,
            nominalThickness: item.nominal,
            description: item.description,
            maxThickness: 'N/A',
            minThickness: 'N/A',
            wallLoss: 'N/A',
            wallLossPercentage: 'N/A',
        };
    }, [getReadingsForUTMItem]);

    const renderExpressSummary = useCallback(() => {
        const sections = inspectionItems
                            .flatMap((item) => {
                                const mappedItems = item.items.map((inspectionItem) => mapUTMInspectionToBreakdown(inspectionItem));
                                const minReading = _.minBy(mappedItems, 'minThickness');

                                return {
                                    ...minReading,
                                    description: item.sectionTitle, 
                                    maxThickness: _.maxBy(mappedItems, 'maxThickness')?.maxThickness,
                                }
                            });

        return (
            getChunkedData(sections, 28).map((chunk, index) => (
                <CustomSection key={index} sectionTitle={expressSummaryTitle} sectionSubtitle={expressSummarySubtitle}>
                    <div className="p-2">
                        <PDFTablePage data={chunk} columns={COLUMNS} />

                        <Conditional isVisible={index === 0}>
                            <div className="mt-2">
                                <div className={styles.utmBreakdownHeader}>
                                    TOTAL NUMBER OF CML/UTM TEST READINGS: {totalInspectedItems}
                                </div>
                            </div>
                        </Conditional>
                    </div>
                </CustomSection>
            ))
        )
    }, [inspectionItems, totalInspectedItems, COLUMNS, expressSummaryTitle, expressSummarySubtitle, mapUTMInspectionToBreakdown]);

    return ( 
        <>
            <Conditional isVisible={displayExpressSummary}>
                {renderExpressSummary()}
            </Conditional>

            {inspectionItems.map((item, index) => (
                <React.Fragment key={item.sectionTitle}>
                    {getChunkedData(item.items, 20).map((chunk, index) => (
                        <CustomSection key={index} sectionTitle={summaryTitle} sectionSubtitle={item.sectionTitle}>
                            <div className="p-2">
                                <PDFTablePage data={chunk.map((chunkItem) => mapUTMInspectionToBreakdown(chunkItem))} columns={COLUMNS_WITH_TML_LOCATION} />
                            </div>
                        </CustomSection>
                    ))}
                </React.Fragment>
            ))}
        </>
    );
};
 
export default CustomUTMBreakdown;